import React, { useEffect, useRef, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Loader2, Sparkles, Trash2 } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const SEED_MESSAGE = {
  role: "assistant",
  content: "Hi — I'm the Collation Watch support assistant. Ask me anything about submitting a result, running an election, or navigating the console.",
};

const QUICK_PROMPTS = [
  "How do I submit a result?",
  "What does the ±N mismatch warning mean?",
  "How do I approve a result?",
  "Can I resubmit a rejected result?",
];

export default function SupportChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([SEED_MESSAGE]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Allow the sidebar to open this from anywhere
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-support-chat", handler);
    return () => window.removeEventListener("open-support-chat", handler);
  }, []);

  useEffect(() => {
    if (!user || !open) return;
    api.get("/support/chat/history").then((r) => {
      if (r.data.length) setMessages(r.data);
      else setMessages([SEED_MESSAGE]);
    }).catch(() => {});
  }, [user, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!user) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Something went wrong: ${formatDetail(e.response?.data?.detail) || e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!confirm("Clear this conversation?")) return;
    try { await api.delete("/support/chat/history"); setMessages([SEED_MESSAGE]); } catch {}
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="support-chat-launcher"
          aria-label="Open live support chat"
          className="hidden"
        >
          <MessageCircle size={18} strokeWidth={2} />
          <span>Live support</span>
        </button>
      )}

      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[min(400px,calc(100vw-24px))] h-[min(620px,calc(100vh-48px))] bg-white border border-white/10 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.7)] flex flex-col"
        >
          {/* header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
            <BrandMark variant="icon" size={22} />
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm tracking-tight text-white">Collation Watch Support</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <Sparkles size={9} strokeWidth={2} /> AI · Claude Sonnet
              </div>
            </div>
            <button onClick={clear} title="Clear conversation" className="p-1.5 text-zinc-500 hover:text-red-400" data-testid="support-chat-clear"><Trash2 size={14}/></button>
            <button onClick={() => setOpen(false)} title="Close" className="p-1.5 text-zinc-400 hover:text-white" data-testid="support-chat-close"><X size={16}/></button>
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={m.id || `${m.role}-${m.ts || i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.role}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-white text-black" : "bg-white/5 text-zinc-100 border border-white/10"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="support-typing">
                <div className="px-3.5 py-2.5 text-sm bg-white/5 border border-white/10 text-zinc-400 flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}

            {messages.length === 1 && !sending && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Try one</div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button key={q} onClick={() => send(q)} data-testid={`quick-prompt-${q.slice(0,10)}`}
                      className="text-xs px-3 py-1.5 border border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/10 p-3 flex items-end gap-2"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about anything in the app…"
              rows={1}
              data-testid="support-chat-input"
              className="flex-1 bg-transparent border-b border-white/10 focus:border-white outline-none text-sm py-2 resize-none max-h-32"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="support-chat-send"
              className="p-2 bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40 transition-colors"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
            </button>
          </form>

          <div className="px-3 pb-2 text-[10px] text-zinc-600">
            AI assistant · your messages are stored so admins can improve support.
          </div>
        </div>
      )}
    </>
  );
}
